/** Which session to replay — a calendar over the archive.
 *
 * ⭐ ONLY CAPTURED SESSIONS ARE SELECTABLE. This replaces a plain dropdown
 * that existed for exactly one reason: indices joined the capture at
 * different times, so a free date picker lets you ask for a NIFTY day that
 * exists and a FINNIFTY day that does not, and the only feedback is an
 * unexplained empty chain. The calendar keeps that guarantee — a day the
 * archive does not hold is rendered dead — while adding what the dropdown
 * could not show: WHERE the gaps are. Weekends and holidays are visible as
 * holes, and a month captured thinly looks thin.
 *
 * The trigger names the weekday because a session reads differently
 * depending on it — expiry days, Mondays after a weekend gap — and a bare
 * ISO date hides that.
 */

import { useEffect, useRef, useState, type ReactElement } from "react";
import { cn } from "@/lib/cn";
import {
  formatSession,
  monthLabel,
  monthMatrix,
  nextMonthWithSession,
  parseSession,
  toISO,
} from "@/lib/sessionCalendar";
import { MonthGrid, YearGrid } from "./SessionGrids";

/** Which grid the popup is showing.
 *
 * The month arrows alone were fine while the archive was one month long.
 * Since the Breeze backfill it spans nine, and stepping from September to
 * January took eight clicks — so the header drills UP to months, and months
 * drill up to years, the way a date picker is expected to behave.
 */
type Level = "day" | "month" | "year";

const WEEKDAY_INITIALS = ["S", "M", "T", "W", "T", "F", "S"];

interface Props {
  value: string;
  sessions: string[];
  onChange: (next: string) => void;
}

function DayCell({
  iso,
  day,
  value,
  available,
  onPick,
}: Readonly<{
  iso: string;
  day: number;
  value: string;
  available: Set<string>;
  onPick: (iso: string) => void;
}>): ReactElement {
  const has = available.has(iso);
  return (
    <button
      type="button"
      disabled={!has}
      aria-current={iso === value ? "date" : undefined}
      aria-label={formatSession(iso)}
      className={cn(
        "h-7 w-full rounded-ctl text-[11px] tabular-nums transition-colors",
        iso === value && "bg-accent font-semibold text-on-accent",
        iso !== value && has && "text-ink hover:bg-surface-2",
        // Not archived: still SHOWN, so the gaps in the capture are visible
        // — that is what a calendar buys over a dropdown — but plainly out
        // of reach.
        !has && "cursor-not-allowed text-ink-muted/40",
      )}
      onClick={() => {
        onPick(iso);
      }}
    >
      {day}
    </button>
  );
}

function WeekdayHead(): ReactElement {
  return (
    <thead>
      <tr>
        {WEEKDAY_INITIALS.map((w, i) => (
          <th
            key={`${w}${String(i)}`}
            scope="col"
            className="pb-1 text-[10px] font-medium text-ink-muted"
          >
            {w}
          </th>
        ))}
      </tr>
    </thead>
  );
}

function Grid({
  y,
  m,
  value,
  available,
  onPick,
}: Readonly<{
  y: number;
  m: number;
  value: string;
  available: Set<string>;
  onPick: (iso: string) => void;
}>): ReactElement {
  return (
    <table className="w-full table-fixed border-separate border-spacing-0.5">
      <WeekdayHead />
      <tbody>
        {monthMatrix(y, m).map((week, r) => (
          <tr key={r}>
            {week.map((d, c) =>
              d === null ? (
                <td key={c} />
              ) : (
                <td key={c}>
                  <DayCell
                    iso={toISO({ y, m, d })}
                    day={d}
                    value={value}
                    available={available}
                    onPick={onPick}
                  />
                </td>
              ),
            )}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

interface View {
  y: number;
  m: number;
}

function MonthNav({
  view,
  setView,
  back,
  fwd,
  onDrillUp,
}: Readonly<{
  view: View;
  setView: (v: View) => void;
  back: View | null;
  fwd: View | null;
  onDrillUp: () => void;
}>): ReactElement {
  return (
    <div className="mb-1 flex items-center justify-between">
      <button
        type="button"
        aria-label="Earlier month with sessions"
        className="btn btn-ghost px-1.5 py-0.5 text-xs"
        disabled={back === null}
        onClick={() => {
          if (back) setView(back);
        }}
      >
        &lsaquo;
      </button>
      <button
        type="button"
        aria-label="Pick a month"
        className="btn btn-ghost px-2 py-0.5 text-xs font-medium text-ink"
        onClick={onDrillUp}
      >
        {monthLabel(view.y, view.m)}
      </button>
      <button
        type="button"
        aria-label="Later month with sessions"
        className="btn btn-ghost px-1.5 py-0.5 text-xs"
        disabled={fwd === null}
        onClick={() => {
          if (fwd) setView(fwd);
        }}
      >
        &rsaquo;
      </button>
    </div>
  );
}

/** The year level: pick a year, land on its months. */
function YearLevel({
  view,
  setView,
  setLevel,
  sessions,
}: Readonly<{
  view: View;
  setView: (v: View) => void;
  setLevel: (l: Level) => void;
  sessions: string[];
}>): ReactElement {
  return (
    <Shell sessions={sessions}>
      <div className="mb-1 text-center text-xs font-medium text-ink">Year</div>
      <YearGrid
        sessions={sessions}
        currentYear={view.y}
        onPick={(y) => {
          setView({ y, m: view.m });
          setLevel("month");
        }}
      />
    </Shell>
  );
}

/** The month level: its header drills up to years, a cell drills down to days. */
function MonthLevel({
  view,
  setView,
  setLevel,
  sessions,
}: Readonly<{
  view: View;
  setView: (v: View) => void;
  setLevel: (l: Level) => void;
  sessions: string[];
}>): ReactElement {
  return (
    <Shell sessions={sessions}>
      <div className="mb-1 flex items-center justify-center">
        <button
          type="button"
          aria-label="Pick a year"
          className="btn btn-ghost px-2 py-0.5 text-xs font-medium text-ink"
          onClick={() => {
            setLevel("year");
          }}
        >
          {view.y}
        </button>
      </div>
      <MonthGrid
        year={view.y}
        sessions={sessions}
        currentMonth={view.m}
        onPick={(m) => {
          setView({ y: view.y, m });
          setLevel("day");
        }}
      />
    </Shell>
  );
}

function Popup({
  view,
  setView,
  value,
  sessions,
  available,
  onPick,
}: Readonly<{
  view: View;
  setView: (v: View) => void;
  value: string;
  sessions: string[];
  available: Set<string>;
  onPick: (iso: string) => void;
}>): ReactElement {
  const [level, setLevel] = useState<Level>("day");
  if (level === "year") {
    return <YearLevel view={view} setView={setView} setLevel={setLevel} sessions={sessions} />;
  }
  if (level === "month") {
    return <MonthLevel view={view} setView={setView} setLevel={setLevel} sessions={sessions} />;
  }
  // The arrows SKIP empty months rather than stepping into them: the
  // archive has holidays and, before 2026-08, whole thin stretches.
  // Dead-ending on an empty grid reads as broken.
  return (
    <Shell sessions={sessions}>
      <MonthNav
        view={view}
        setView={setView}
        back={nextMonthWithSession(sessions, view.y, view.m, -1)}
        fwd={nextMonthWithSession(sessions, view.y, view.m, 1)}
        onDrillUp={() => {
          setLevel("month");
        }}
      />
      <Grid y={view.y} m={view.m} value={value} available={available} onPick={onPick} />
    </Shell>
  );
}

/** The popup frame, shared by all three levels so the box does not resize
 * or jump as the reader drills through it. */
function Shell({
  sessions,
  children,
}: Readonly<{ sessions: string[]; children: React.ReactNode }>): ReactElement {
  return (
    <div className="absolute left-0 top-8 z-20 w-60 rounded-ctl border border-line bg-surface-1 p-2 shadow-lg">
      {children}
      <p className="mt-1 text-center text-[10px] text-ink-muted">
        {sessions.length} session{sessions.length === 1 ? "" : "s"} archived
      </p>
    </div>
  );
}

/** Close on Escape or a click outside. Both, because a popover that only
 * one of them shuts is one a reader gets stuck inside. */
function useDismiss(
  open: boolean,
  box: React.RefObject<HTMLDivElement | null>,
  close: () => void,
): void {
  useEffect(() => {
    if (!open) return undefined;
    const away = (e: MouseEvent): void => {
      if (box.current && !box.current.contains(e.target as Node)) close();
    };
    const esc = (e: KeyboardEvent): void => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("mousedown", away);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", away);
      document.removeEventListener("keydown", esc);
    };
    // `close` is a fresh arrow each render; depending on it would tear the
    // listeners down and rebuild them every time, so `open` is the gate.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
}

export function SessionPicker({ value, sessions, onChange }: Readonly<Props>): ReactElement {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const selected = parseSession(value);
  const [view, setView] = useState({ y: selected?.y ?? 2026, m: selected?.m ?? 1 });

  // Re-open on the selected session's month: stepping a day or switching
  // index moves the selection, and a calendar that opens on last month's
  // view makes the reader hunt for where they already are.
  useEffect(() => {
    const ymd = parseSession(value);
    if (ymd) setView({ y: ymd.y, m: ymd.m });
  }, [value]);

  useDismiss(open, box, () => {
    setOpen(false);
  });

  const available = new Set(sessions);

  return (
    <div ref={box} className="relative">
      <button
        type="button"
        aria-label="Session"
        aria-expanded={open}
        className="input h-7 w-44 px-2 py-0 text-left text-xs"
        onClick={() => {
          setOpen((o) => !o);
        }}
      >
        {formatSession(value)}
      </button>
      {open && (
        <Popup
          view={view}
          setView={setView}
          value={value}
          sessions={sessions}
          available={available}
          onPick={(iso) => {
            onChange(iso);
            setOpen(false);
          }}
        />
      )}
    </div>
  );
}
